import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  Eye,
  Phone,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  X,
  ClipboardCheck,
  UserPlus,
} from 'lucide-react';

import { useRole } from '../contexts/RoleContext.jsx';
import { useApiData } from '../hooks/useApiData.js';
import { ErrorBanner, InlineSpinner } from '../components/common/PageState.jsx';
import { listLeads } from '../services/api.js';
import { humanizeToken } from '../utils/humanize.js';
import {
  leads as seedLeads,
  agents,
  agentById,
  verificationByLeadId,
  NOW_ISO,
} from '../data/seedData.js';

const NOW = new Date(NOW_ISO);
const PAGE_SIZE = 10;

// ── option lists ───────────────────────────────────────────────────────────

const SOURCE_OPTIONS = [
  { value: 'all', label: 'All sources' },
  { value: 'zillow', label: 'Zillow' },
  { value: 'facebook_ads', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'website', label: 'Website' },
  { value: 'referral', label: 'Referral' },
  { value: 'manual', label: 'Manual' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'qualifying', label: 'Qualifying' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'verified', label: 'Verified' },
  { value: 'showing', label: 'Showing' },
  { value: 'nurture', label: 'Nurture' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
  { value: 'disqualified', label: 'Disqualified' },
  { value: 'dead', label: 'Dead' },
];

const VERIFICATION_OPTIONS = [
  { value: 'all', label: 'All verification' },
  { value: 'verified', label: 'Verified' },
  { value: 'likely', label: 'Likely' },
  { value: 'uncertain', label: 'Uncertain' },
  { value: 'red_flag', label: 'Red Flag' },
  { value: 'unverified', label: 'Unverified' },
];

const AGENT_OPTIONS = [
  { value: 'all', label: 'All agents' },
  { value: 'unassigned', label: 'Unassigned' },
  ...agents.map((a) => ({ value: a.id, label: a.name })),
];

// ── derivations ────────────────────────────────────────────────────────────

function verificationState(lead) {
  // Prefer the denormalized verification the API now sends on every lead.
  const apiVer = lead?.verification;
  if (apiVer && apiVer.status) {
    const s = apiVer.status;
    if (s === 'verified' || s === 'likely' || s === 'uncertain' || s === 'red_flag') {
      return s;
    }
  }

  // Seed fallback — the seed fixture uses a slightly different shape
  // ({status: 'passed'|'flagged'|'pending', overallScore}) so we translate.
  const v = verificationByLeadId[lead?.id];
  if (!v) return 'unverified';
  if (v.status === 'flagged' || v.status === 'failed') return 'red_flag';
  if (v.status === 'pending') return 'uncertain';
  if (v.overallScore >= 80) return 'verified';
  if (v.overallScore >= 60) return 'likely';
  return 'uncertain';
}

const VERIFICATION_BADGE = {
  verified: { label: 'Verified', cls: 'bg-[#DCFCE7] text-[#16A34A]' },
  likely: { label: 'Likely', cls: 'bg-[#FEF9C3] text-[#A16207]' },
  uncertain: { label: 'Uncertain', cls: 'bg-[#FFEDD5] text-[#C2410C]' },
  red_flag: { label: 'Red Flag', cls: 'bg-[#FEE2E2] text-[#DC2626]' },
  unverified: { label: 'Unverified', cls: 'bg-[#F3F4F6] text-[#6B7280]' },
};

const TEMP_BADGE = {
  hot: { label: 'Hot', cls: 'bg-[#FEE2E2] text-[#DC2626]' },
  warm: { label: 'Warm', cls: 'bg-[#FEF3C7] text-[#D97706]' },
  nurture: { label: 'Nurture', cls: 'bg-[#DBEAFE] text-[#2563EB]' },
  cold: { label: 'Cold', cls: 'bg-[#F3F4F6] text-[#6B7280]' },
  dead: { label: 'Dead', cls: 'bg-[#F3F4F6] text-[#6B7280]' },
  red_flag: { label: 'Red Flag', cls: 'bg-[#FEE2E2] text-[#DC2626]' },
};

const STATUS_LABEL = Object.fromEntries(
  STATUS_OPTIONS.filter((o) => o.value !== 'all').map((o) => [o.value, o.label]),
);

const SOURCE_META = {
  zillow: { label: 'Zillow', cls: 'bg-hot/15 text-hot' },
  facebook_ads: { label: 'Facebook', cls: 'bg-[#8B5CF6]/15 text-[#8B5CF6]' },
  instagram: { label: 'Instagram', cls: 'bg-[#EC4899]/15 text-[#EC4899]' },
  whatsapp: { label: 'WhatsApp', cls: 'bg-[#14B8A6]/15 text-[#14B8A6]' },
  website: { label: 'Website', cls: 'bg-nurture/15 text-nurture' },
  referral: { label: 'Referral', cls: 'bg-verified/15 text-verified' },
  manual: { label: 'Manual', cls: 'bg-cold/15 text-cold' },
};

function sourceMeta(src) {
  return SOURCE_META[src] || { label: src, cls: 'bg-cold/15 text-cold' };
}

function scoreColorClass(score) {
  if (score >= 80) return 'text-score-green';
  if (score >= 50) return 'text-score-yellow';
  return 'text-score-red';
}

function propertyInterestLabel(lead) {
  const typeLabel = lead.propertyType ? humanizeToken(lead.propertyType) : '—';
  const hoods = (lead.neighborhoods || [])
    .map(humanizeToken)
    .filter(Boolean)
    .join(', ');
  return hoods ? `${typeLabel} · ${hoods}` : typeLabel;
}

function relativeTime(iso) {
  if (!iso) return '—';
  const then = new Date(iso);
  const diffMin = Math.round((NOW - then) / 60_000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return 'Yesterday';
  if (diffD < 7) return `${diffD} days ago`;
  if (diffD < 30) return `${Math.round(diffD / 7)}w ago`;
  if (diffD < 365) return `${Math.round(diffD / 30)}mo ago`;
  return `${Math.round(diffD / 365)}y ago`;
}

// ── subcomponents ──────────────────────────────────────────────────────────

function Select({ value, onChange, options, label }) {
  return (
    <label className="block min-w-0">
      <span className="sr-only">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={label}
        style={{ fontSize: '15px' }}
        className="w-full bg-bg-card border border-border-card rounded-btn text-text-primary
                   px-3 py-2 hover:border-accent/60 focus:border-accent focus:outline-none
                   focus:ring-1 focus:ring-accent/50 transition-colors"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-bg-card">
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function SortHeader({ columnKey, children, sortKey, sortDir, onSort, className = '' }) {
  const active = sortKey === columnKey;
  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      style={{ fontSize: '14px' }}
      className={`px-4 py-3 text-left font-medium uppercase tracking-wider text-text-secondary ${className}`}
    >
      <button
        type="button"
        onClick={() => onSort(columnKey)}
        className={`inline-flex items-center gap-1 hover:text-text-primary transition-colors ${
          active ? 'text-text-primary' : ''
        }`}
      >
        {children}
        <Icon size={12} strokeWidth={2.25} className={active ? 'text-accent' : 'opacity-60'} />
      </button>
    </th>
  );
}

function Badge({ className = '', children }) {
  return <span className={`badge ${className}`}>{children}</span>;
}

// ── page ───────────────────────────────────────────────────────────────────

const INITIAL_FILTERS = {
  search: '',
  source: 'all',
  status: 'all',
  verification: 'all',
  agent: 'all',
  scoreMin: '',
  scoreMax: '',
};

export default function Leads() {
  const { role, currentUser } = useRole();
  const showAgentColumn = role !== 'agent';
  const showAgentFilter = role !== 'agent';
  const canAssign = role === 'isa' || role === 'team_leader';
  const canClaim = role === 'isa';

  // ISA/Team Leader can claim/assign. Stored locally for optimistic UX.
  const [assignments, setAssignments] = useState({});

  // Fetch from API when live; fall back to seed leads silently when the
  // request fails or demo mode is on.
  const leadsReq = useApiData(() => listLeads({ limit: 500 }), []);
  const allLeads = leadsReq.data?.leads?.length ? leadsReq.data.leads : seedLeads;

  // Pre-scope the roster by role. Agents only ever see their own leads.
  const scopedLeads = useMemo(() => {
    const resolved = allLeads.map((lead) =>
      Object.prototype.hasOwnProperty.call(assignments, lead.id)
        ? { ...lead, assignedAgentId: assignments[lead.id] }
        : lead,
    );
    if (role === 'agent' && currentUser) {
      return resolved.filter((l) => l.assignedAgentId === currentUser.id);
    }
    return resolved;
  }, [assignments, role, currentUser, allLeads]);

  const claim = (leadId) => {
    if (!currentUser) return;
    setAssignments((prev) => ({ ...prev, [leadId]: currentUser.id }));
  };

  const assign = (leadId, agentId) => {
    setAssignments((prev) => ({ ...prev, [leadId]: agentId }));
  };

  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const agentParam = searchParams.get('agent');
    if (agentParam && agents.some((a) => a.id === agentParam)) {
      return { ...INITIAL_FILTERS, agent: agentParam };
    }
    if (agentParam === 'unassigned') {
      return { ...INITIAL_FILTERS, agent: 'unassigned' };
    }
    return INITIAL_FILTERS;
  });
  const [sortKey, setSortKey] = useState('score');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [openAssignRow, setOpenAssignRow] = useState(null);

  // Keep filter in sync if user lands on /leads?agent=... from Team page.
  useEffect(() => {
    const agentParam = searchParams.get('agent');
    if (!agentParam) return;
    if (agentParam === 'unassigned' || agents.some((a) => a.id === agentParam)) {
      setFilters((prev) =>
        prev.agent === agentParam ? prev : { ...prev, agent: agentParam },
      );
      setPage(1);
    }
  }, [searchParams]);
  const assignableAgents = useMemo(
    () => agents.filter((a) => a.role === 'team_leader' || a.role === 'agent'),
    [],
  );

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    setPage(1);
  };

  // Pre-compute verification state per lead once. Resolve the display
  // agent from the API's denormalized payload first (which sends DB UUIDs
  // that won't match seed IDs), then fall back to the seed roster.
  const enriched = useMemo(
    () =>
      scopedLeads.map((lead) => {
        const seedAgent = lead.assignedAgentId ? agentById[lead.assignedAgentId] : null;
        const apiAgent = lead.agent && lead.agent.name ? lead.agent : null;
        return {
          lead,
          verification: verificationState(lead),
          agent: seedAgent || apiAgent,
        };
      }),
    [scopedLeads],
  );

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    const min = filters.scoreMin === '' ? null : Number(filters.scoreMin);
    const max = filters.scoreMax === '' ? null : Number(filters.scoreMax);

    return enriched.filter(({ lead, verification }) => {
      if (q) {
        const hay = `${lead.name} ${lead.phone || ''} ${lead.email || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.source !== 'all' && lead.source !== filters.source) return false;
      if (filters.status !== 'all' && lead.status !== filters.status) return false;
      if (filters.verification !== 'all' && verification !== filters.verification) return false;
      if (filters.agent === 'unassigned' && lead.assignedAgentId) return false;
      if (
        filters.agent !== 'all' &&
        filters.agent !== 'unassigned' &&
        lead.assignedAgentId !== filters.agent
      )
        return false;
      if (min !== null && !Number.isNaN(min) && lead.score < min) return false;
      if (max !== null && !Number.isNaN(max) && lead.score > max) return false;
      return true;
    });
  }, [enriched, filters]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmp = (a, b, key) => {
      switch (key) {
        case 'score':
          return (a.lead.score - b.lead.score) * dir;
        case 'name':
          return a.lead.name.localeCompare(b.lead.name) * dir;
        case 'source':
          return (a.lead.source || '').localeCompare(b.lead.source || '') * dir;
        case 'lastActivity':
          return (
            (new Date(a.lead.lastContactAt) - new Date(b.lead.lastContactAt)) * dir
          );
        case 'status':
          return a.lead.status.localeCompare(b.lead.status) * dir;
        case 'verification':
          return a.verification.localeCompare(b.verification) * dir;
        case 'agent':
          return (
            (a.agent?.name || 'zzz').localeCompare(b.agent?.name || 'zzz') * dir
          );
        case 'interest':
          return propertyInterestLabel(a.lead).localeCompare(
            propertyInterestLabel(b.lead),
          ) * dir;
        default:
          return 0;
      }
    };
    rows.sort((a, b) => cmp(a, b, sortKey));
    return rows;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = sorted.slice(pageStart, pageStart + PAGE_SIZE);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Smart default: numeric desc, text asc.
      setSortDir(key === 'score' || key === 'lastActivity' ? 'desc' : 'asc');
    }
  };

  const countLabel =
    sorted.length === 0
      ? 'Showing 0 of 0'
      : `Showing ${pageStart + 1}-${Math.min(pageStart + PAGE_SIZE, sorted.length)} of ${sorted.length}`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="page-title mb-0">
              {role === 'agent' ? 'My Leads' : 'Leads'}
            </h1>
            {leadsReq.loading && <InlineSpinner />}
          </div>
          <p className="page-subtitle mb-0 mt-2">
            {role === 'agent'
              ? `Your assigned pipeline — ${scopedLeads.length} leads.`
              : `Full pipeline — filter, search, and sort across ${scopedLeads.length} leads.`}
          </p>
        </div>
        <span className="text-sm text-text-secondary">{countLabel}</span>
      </div>

      {leadsReq.error && (
        <ErrorBanner
          message="Couldn’t load leads from the server."
          onRetry={leadsReq.refetch}
        />
      )}

      {/* Filter bar */}
      <div className="card p-6 md:px-7 md:py-6 space-y-3 w-full">
        {/* Search */}
        <div className="relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none"
          />
          <input
            type="text"
            placeholder="Search by name, phone, or email"
            value={filters.search}
            onChange={(e) => setFilter('search', e.target.value)}
            style={{ fontSize: '15px' }}
            className="w-full bg-bg-card border border-border-card rounded-btn text-text-primary
                       placeholder:text-text-secondary pl-9 pr-9 py-2
                       hover:border-accent/60 focus:border-accent focus:outline-none
                       focus:ring-1 focus:ring-accent/50 transition-colors"
          />
          {filters.search && (
            <button
              type="button"
              onClick={() => setFilter('search', '')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Dropdowns + score range */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${
            showAgentFilter ? 'lg:grid-cols-6' : 'lg:grid-cols-5'
          }`}
        >
          <Select
            label="Source"
            value={filters.source}
            onChange={(v) => setFilter('source', v)}
            options={SOURCE_OPTIONS}
          />
          <Select
            label="Status"
            value={filters.status}
            onChange={(v) => setFilter('status', v)}
            options={STATUS_OPTIONS}
          />
          <Select
            label="Verification"
            value={filters.verification}
            onChange={(v) => setFilter('verification', v)}
            options={VERIFICATION_OPTIONS}
          />
          {showAgentFilter && (
            <Select
              label="Agent"
              value={filters.agent}
              onChange={(v) => setFilter('agent', v)}
              options={AGENT_OPTIONS}
            />
          )}
          <label className="block min-w-0">
            <span className="sr-only">Minimum score</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              placeholder="Min score"
              value={filters.scoreMin}
              onChange={(e) => setFilter('scoreMin', e.target.value)}
              className="w-full bg-bg-card border border-border-card rounded-btn text-text-primary
                         placeholder:text-text-secondary text-sm px-3 py-2
                         hover:border-accent/60 focus:border-accent focus:outline-none
                         focus:ring-1 focus:ring-accent/50 transition-colors"
            />
          </label>
          <label className="block min-w-0">
            <span className="sr-only">Maximum score</span>
            <input
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              placeholder="Max score"
              value={filters.scoreMax}
              onChange={(e) => setFilter('scoreMax', e.target.value)}
              className="w-full bg-bg-card border border-border-card rounded-btn text-text-primary
                         placeholder:text-text-secondary text-sm px-3 py-2
                         hover:border-accent/60 focus:border-accent focus:outline-none
                         focus:ring-1 focus:ring-accent/50 transition-colors"
            />
          </label>
        </div>
      </div>

      {/* Table or empty state */}
      {sorted.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-base text-text-primary font-medium">
            No leads match your filters
          </p>
          <p className="mt-1 text-sm text-text-secondary">
            Try broadening your search or resetting the filters.
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 inline-flex items-center gap-2 rounded-btn border border-accent/60
                       text-accent bg-accent/5 hover:bg-accent/15 hover:border-accent
                       px-4 py-2 text-sm font-medium transition-colors"
          >
            <X size={14} /> Reset filters
          </button>
        </div>
      ) : (
        <div className="card overflow-hidden w-full">
          <div className="overflow-x-auto">
            <table
              className="w-full"
              style={{ fontSize: '15px', tableLayout: 'auto' }}
            >
              <thead className="bg-bg-subtle border-b border-border-card">
                <tr>
                  <SortHeader columnKey="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>
                    Contact
                  </SortHeader>
                  <SortHeader columnKey="source" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>
                    Source
                  </SortHeader>
                  <SortHeader
                    columnKey="interest"
                    sortKey={sortKey}
                    sortDir={sortDir}
                    onSort={handleSort}
                    className="hidden md:table-cell"
                  >
                    Property Interest
                  </SortHeader>
                  <SortHeader columnKey="lastActivity" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>
                    Last Activity
                  </SortHeader>
                  <SortHeader columnKey="status" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>
                    Status
                  </SortHeader>
                  <SortHeader columnKey="verification" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>
                    Verified
                  </SortHeader>
                  <SortHeader columnKey="score" sortKey={sortKey} sortDir={sortDir} onSort={handleSort}>
                    Score
                  </SortHeader>
                  {showAgentColumn && (
                    <SortHeader
                      columnKey="agent"
                      sortKey={sortKey}
                      sortDir={sortDir}
                      onSort={handleSort}
                      className="hidden md:table-cell"
                    >
                      Agent
                    </SortHeader>
                  )}
                  <th
                    scope="col"
                    style={{ fontSize: '14px' }}
                    className="px-4 py-3 text-right font-medium uppercase tracking-wider text-text-secondary"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(({ lead, verification, agent }) => {
                  const src = sourceMeta(lead.source);
                  const temp = TEMP_BADGE[lead.temperature] || TEMP_BADGE.cold;
                  const ver = VERIFICATION_BADGE[verification];
                  const statusLabel = STATUS_LABEL[lead.status] || lead.status;
                  const telHref = lead.phone ? `tel:${lead.phone.replace(/[^\d+]/g, '')}` : null;
                  const smsHref = lead.phone ? `sms:${lead.phone.replace(/[^\d+]/g, '')}` : null;
                  return (
                    <tr
                      key={lead.id}
                      className="border-t border-border-card hover:bg-bg-subtle transition-colors"
                    >
                      <td className="px-4 py-3 min-w-[220px] w-[22%]">
                        <Link
                          to={`/leads/${lead.id}`}
                          className="block font-semibold text-text-primary hover:text-accent"
                        >
                          {lead.name}
                        </Link>
                        {lead.phone && (
                          <a
                            href={telHref}
                            className="block text-xs text-text-secondary hover:text-accent"
                          >
                            {lead.phone}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={src.cls}>{src.label}</Badge>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-text-secondary max-w-[240px]">
                        <div className="truncate" title={propertyInterestLabel(lead)}>
                          {propertyInterestLabel(lead)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text-secondary whitespace-nowrap">
                        {relativeTime(lead.lastContactAt)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={temp.cls}>{statusLabel}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={ver.cls}>{ver.label}</Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-xl font-medium tabular-nums ${scoreColorClass(lead.score)}`}
                        >
                          {lead.score}
                        </span>
                      </td>
                      {showAgentColumn && (
                        <td className="px-4 py-3 hidden md:table-cell whitespace-nowrap">
                          {agent ? (
                            <span className="text-text-primary">{agent.name}</span>
                          ) : (
                            <span className="text-hot italic">Unassigned</span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1 md:gap-0.5">
                          {canClaim && !lead.assignedAgentId && (
                            <button
                              type="button"
                              onClick={() => claim(lead.id)}
                              className="inline-flex items-center gap-1 rounded-pill bg-accent
                                         text-white px-3 py-1.5 text-xs font-semibold
                                         hover:bg-accent-hover transition-colors"
                              title={`Claim ${lead.name}`}
                            >
                              <ClipboardCheck size={13} /> Claim
                            </button>
                          )}
                          {canAssign && (
                            <div className="relative">
                              <button
                                type="button"
                                onClick={() =>
                                  setOpenAssignRow((r) => (r === lead.id ? null : lead.id))
                                }
                                style={{ borderColor: '#9CA3AF', color: '#9CA3AF', borderWidth: '1.5px' }}
                                className="inline-flex items-center gap-1 rounded-pill
                                           bg-transparent border
                                           hover:bg-[#F3F4F6] hover:text-[#333333]
                                           px-2.5 py-1.5 text-xs font-medium transition-colors"
                                title="Assign agent"
                                aria-label={`Assign agent for ${lead.name}`}
                              >
                                <UserPlus size={13} />
                                <span className="hidden lg:inline">Assign</span>
                              </button>
                              {openAssignRow === lead.id && (
                                <div
                                  className="absolute right-0 mt-2 w-56 card p-1 z-10 shadow-lg"
                                  onMouseLeave={() => setOpenAssignRow(null)}
                                >
                                  <button
                                    type="button"
                                    onClick={() => {
                                      assign(lead.id, null);
                                      setOpenAssignRow(null);
                                    }}
                                    className="block w-full text-left px-3 py-2 text-sm
                                               text-text-secondary hover:bg-bg-subtle rounded-btn"
                                  >
                                    Unassign
                                  </button>
                                  {assignableAgents.map((a) => (
                                    <button
                                      key={a.id}
                                      type="button"
                                      onClick={() => {
                                        assign(lead.id, a.id);
                                        setOpenAssignRow(null);
                                      }}
                                      className={`block w-full text-left px-3 py-2 text-sm rounded-btn
                                                 hover:bg-accent/10 ${
                                                   a.id === lead.assignedAgentId
                                                     ? 'text-accent'
                                                     : 'text-text-primary'
                                                 }`}
                                    >
                                      <span className="font-medium">{a.name}</span>
                                      <span className="block text-xs text-text-secondary">
                                        {a.title}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          <Link
                            to={`/leads/${lead.id}`}
                            aria-label={`View ${lead.name}`}
                            style={{ color: '#9CA3AF' }}
                            className="inline-flex items-center justify-center rounded-btn
                                       hover:text-[#333333] hover:bg-[#F3F4F6]
                                       p-2.5 md:p-2 transition-colors"
                          >
                            <Eye size={16} />
                          </Link>
                          {telHref ? (
                            <a
                              href={telHref}
                              aria-label={`Call ${lead.name}`}
                              style={{ color: '#9CA3AF' }}
                              className="inline-flex items-center justify-center rounded-btn
                                         hover:text-[#16A34A] hover:bg-[#DCFCE7]
                                         p-2.5 md:p-2 transition-colors"
                            >
                              <Phone size={16} />
                            </a>
                          ) : (
                            <span className="inline-flex items-center justify-center p-2.5 md:p-2 text-cold/40">
                              <Phone size={16} />
                            </span>
                          )}
                          {smsHref ? (
                            <a
                              href={smsHref}
                              aria-label={`Message ${lead.name}`}
                              style={{ color: '#9CA3AF' }}
                              className="inline-flex items-center justify-center rounded-btn
                                         hover:text-[#3B82F6] hover:bg-[#DBEAFE]
                                         p-2.5 md:p-2 transition-colors"
                            >
                              <MessageSquare size={16} />
                            </a>
                          ) : (
                            <span className="inline-flex items-center justify-center p-2.5 md:p-2 text-cold/40">
                              <MessageSquare size={16} />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-border-card">
            <span className="text-xs text-text-secondary">{countLabel}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 rounded-btn border border-border-card
                           text-sm px-3 py-1.5 text-text-primary hover:border-accent
                           disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border-card
                           transition-colors"
              >
                <ChevronLeft size={14} />
                Previous
              </button>
              <span className="text-xs text-text-secondary tabular-nums px-2">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 rounded-btn border border-border-card
                           text-sm px-3 py-1.5 text-text-primary hover:border-accent
                           disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-border-card
                           transition-colors"
              >
                Next
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
